import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { JsonForms } from "@jsonforms/react";
import {
  materialCells,
  materialRenderers,
} from "@jsonforms/material-renderers";

import PropTypes from "prop-types";
import TextfieldRenderer, {
  TextFieldTester,
} from "./renderers/TextfieldRenderer";
import VerticalLayoutRenderer, {
  VerticalLayoutTester,
} from "./renderers/VerticalLayoutRenderer";
import HorizontalLayoutRenderer, {
  HorizontalLayoutTester,
} from "./renderers/HorizontalLayoutRenderer";
import SelectRenderer, {
  SelectTester,
  SelectWithSearchRenderer,
  SelectWithSearchTester,
} from "./renderers/SelectRenderer";
import CheckboxRenderer, { CheckboxTester } from "./renderers/CheckboxRenderer";
import GroupRenderer, { GroupTester } from "./renderers/GroupRenderer";
import DatePickerRenderer, {
  DatePickerTester,
} from "./renderers/DatePickerRenderer";
import EmptyRenderer, { EmptyTester } from "./renderers/EmptyRenderer";
import { useDispatch } from "react-redux";
import {
  formsActions,
  useFormsSelector,
} from "../../redux/reducers/formsReducer";
import TextEditorRenderer, {
  TextEditorTester,
} from "./renderers/TextEditorRenderer";
import BadgeRenderer, { BadgeTester } from "./renderers/BadgeRenderer";
import TextWithIconRenderer, {
  TextWithIconTester,
} from "./renderers/TextWithIconRenderer";
import RadioRenderer, { RadioTester } from "./renderers/RadioRenderer";
import NumberRenderer, { NumberTester } from "./renderers/NumberRenderer";
import SliderRenderer, { SliderTester } from "./renderers/SliderRenderer";
import SwitchRenderer, { SwitchTester } from "./renderers/SwitchRenderer";
import TextWithTooltipRenderer, {
  TextWithTooltipTester,
} from "./renderers/TextWithTooltipRenderer";
import GenericComponentRenderer, {
  genericComponentTester,
} from "./renderers/GenericComponentRenderer";
import SvgUploadButtonRenderer, {SvgUploadButtonTester} from "./renderers/svgUploadButtonRenderer";
import AssetUploadButtonRenderer, {AssetUploadButtonTester} from "./renderers/assetUploadButtonRenderer";
import OpenDrawerRenderer, {OpenDrawerRendererTester} from "./renderers/OpenDrawerRenderer";
import { translateError } from "./translateError";

const SFFormBuilder = ({
  //name is used in order to save the state of the form in redux
  name = null,
  schema,
  uischema,
  data,
  onChange,
  customRenderers = [],
  readonly,
  //persistState is used in order to save all the form's info in redux state
  //in case where persistate is false we only save the name of the form and the submittted value
  persistState = true,
  //we use submit prop in case where we don't save the state of form to redux
  submit = false,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const formResult = useFormsSelector(name);
  const form = name ?  formResult : false;
  const [hasRendered, setHasRendered] = useState(false);

  useEffect(() => {
    if (name) {
      if (form) {
        onChange(form.data, form.errors, form.hasChanges);
        setHasRendered(form.hasChanges);
        //set submit to false in order to hide the validation errors
        //check getvalidation mode function below
        dispatch(formsActions.submit({ name, value: false }));
      } else {
        dispatch(formsActions.initialize(name));
      }

      return () => {
        dispatch(formsActions.delete(name));
      };
    }
  }, []);

  let sfRenderers = [
    {
      renderer: TextfieldRenderer,
      tester: TextFieldTester,
    },
    {
      renderer: VerticalLayoutRenderer,
      tester: VerticalLayoutTester,
    },
    {
      renderer: HorizontalLayoutRenderer,
      tester: HorizontalLayoutTester,
    },
    {
      renderer: SelectRenderer,
      tester: SelectTester,
    },
    {
      renderer: SelectWithSearchRenderer,
      tester: SelectWithSearchTester,
    },
    {
      renderer: CheckboxRenderer,
      tester: CheckboxTester,
    },
    {
      renderer: GroupRenderer,
      tester: GroupTester,
    },
    {
      renderer: DatePickerRenderer,
      tester: DatePickerTester,
    },
    {
      renderer: EmptyRenderer,
      tester: EmptyTester,
    },
    {
      renderer: TextEditorRenderer,
      tester: TextEditorTester,
    },
    {
      renderer: BadgeRenderer,
      tester: BadgeTester,
    },
    {
      renderer: TextWithIconRenderer,
      tester: TextWithIconTester,
    },
    {
      renderer: RadioRenderer,
      tester: RadioTester,
    },
    {
      renderer: NumberRenderer,
      tester: NumberTester,
    },
    {
      renderer: SliderRenderer,
      tester: SliderTester,
    },
    {
      renderer: SwitchRenderer,
      tester: SwitchTester,
    },
    {
      renderer: TextWithTooltipRenderer,
      tester: TextWithTooltipTester,
    },
    {
      renderer: GenericComponentRenderer,
      tester: genericComponentTester,
    },
    {
      renderer: SvgUploadButtonRenderer,
      tester: SvgUploadButtonTester
    },
    {
      renderer: AssetUploadButtonRenderer,
      tester: AssetUploadButtonTester
    },
    {
      renderer: OpenDrawerRenderer,
      tester: OpenDrawerRendererTester
    }
  ];

  const getValidationMode = useCallback(() => {
    //if name exists, which means that form state is in redux
    // then we get the validation mode from the redux store
    if (name) {
      return form?.submit ? "ValidateAndShow" : "ValidateAndHide";
      //form doesn't exist in redux, so we use the submit prop
    } else {
      return submit ? "ValidateAndShow" : "ValidateAndHide";
    }
  }, [submit, form?.submit]);

  return (
    <JsonForms
      i18n={{ translateError: translateError(t) }}
      readonly={readonly}
      schema={schema}
      uischema={uischema}
      data={data}
      renderers={[...materialRenderers, ...sfRenderers, ...customRenderers]}
      cells={materialCells}
      validationMode={getValidationMode()}
      onChange={({ data, errors }) => {
        onChange(data, errors, hasRendered);
        if (name && persistState) {
          dispatch(
            formsActions.update({
              name,
              data,
              errors,
              hasChanges: hasRendered,
            })
          );
        }
        if (!hasRendered) {
          setHasRendered(true);
        }
      }}
    />
  );
};

SFFormBuilder.propTypes = {
  schema: PropTypes.object.isRequired,
  uischema: PropTypes.object.isRequired,
  data: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  customRenderers: PropTypes.arrayOf(
    PropTypes.shape({
      tester: PropTypes.func.isRequired,
      renderer: PropTypes.func.isRequired,
    })
  ),
  persistState: PropTypes.bool,
  submit: PropTypes.bool,
};

export default SFFormBuilder;
